import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import AbEnvironment from '../auth/AbEnvironment';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import { Checkbox, FormControlLabel, FormGroup, Grid, TextField, Typography } from '@mui/material';
import { addDays, isValid, startOfDay, subMonths } from 'date-fns';
import { TablePagination } from '@mui/material';
import LoadingSpinner from '../ui/LoadingSpinner';
import AuditsList from './AuditsList';
import SearchField from '../ui/SearchField';
import AppBindStyles from '../../AppBindStyles';

const abEnvironment = new AbEnvironment();
const AuditsQuery = graphql`
query AuditsQuery($startDate: Datetime, $endDate: Datetime, $accountName: String, $severities: [Int], $auditType: String, $auditDetail: String, $rlimit: Int, $first: Int, $offset: Int) {
  getauditentries2(startTs: $startDate, endTs: $endDate, accountName: $accountName, severities: $severities, auditType: $auditType, auditDetail: $auditDetail, rlimit: $rlimit, first: $first, offset: $offset) {
    totalCount
    ...AuditsList_al
  }
}
`;

const styleSheet = theme => ({
    root: {
      flexGrow: 1,
      //marginTop: 10,
    },
    flex: {
      flex: 1,
    }
});

class Audits extends Component {

    constructor(props) {
      super(props);
   
      // Default toDate
      let cToDate = new Date();
      let tDate = localStorage.getItem('cToDate');
      if (tDate != null) {
        cToDate = new Date(tDate);
      }
      //search "to date" for query is start of next day
      let searchToDateUtc = startOfDay(addDays(cToDate, 1)).toISOString();
  
      let cFromDate = new Date();
      let fDate = localStorage.getItem('cFromDate');
      if (fDate != null) {
        cFromDate = new Date(fDate);
      } else {
        cFromDate = subMonths(cToDate, 1);
      }
      //search "from date" is start of selected day
      let searchFromDateUtc = startOfDay(cFromDate).toISOString();
  
      this.state = {
        startDate: cFromDate,
        queryStartDateUtc: searchFromDateUtc,
        endDate: cToDate,
        queryEndDateUtc: searchToDateUtc,
        accountName: '', 
        auditType: '',
        auditDetail: '',
        page: 0,
        rowsPerPage: 10,
        s_critical: false,
        s_error: false,
        s_warning: false,
        s_info: false
      };  
    }

    handleChange = name => event => {
      this.setState({
        [name]: event.target.value,
        page: 0,
      });
    };

    setFromDate(theDate) {
      let vdate = new Date(theDate);
      if (isValid(vdate)) {
        localStorage.setItem('cFromDate', vdate);
        let searchDateUtc = startOfDay(vdate).toISOString();
        this.setState({ startDate: vdate, queryStartDateUtc: searchDateUtc, page : 0 });
      }
    }
  
    setToDate(theDate) {
      let vdate =new Date(theDate);
      if (isValid(vdate)) {
        localStorage.setItem('cToDate', vdate);
        let searchDateUtc = startOfDay(addDays(vdate, 1)).toISOString();
        this.setState({ endDate: vdate, queryEndDateUtc: searchDateUtc, page : 0 });
      }
    }
      
    handleChangePage = (event, page) => {
      this.setState({page: page});
    }
    
    handleChangeRowsPerPage = (event) => {
      this.setState({ rowsPerPage: event.target.value, page : 0});
    }

    handleCheckChange = name => event => {
      this.setState({
        [name]: event.target.checked,
        page: 0,
      });
    };
  
    render() {
      const classes = this.props.classes;
    
      let theAccountSearch = '%';
      if (this.state.accountName !== '')
      {
        theAccountSearch = '%' + this.state.accountName + '%';
      }
      let theAuditTypeSearch = '%';
      if (this.state.auditType !== '')
      {
        theAuditTypeSearch = '%' + this.state.auditType + '%';
      }
      let theAuditDetailSearch = '%';
      if (this.state.auditDetail !== '')
      {
        theAuditDetailSearch = '%' + this.state.auditDetail + '%';
      }

      // Checkbox values are used to create the array values for severity
      let severities = null;
      if (this.state.s_critical || this.state.s_error || this.state.s_warning || this.state.s_info)
      {
        severities = [];
        if (this.state.s_critical) {
          severities.push(0);
        }
        
        if (this.state.s_error) {
          severities.push(1);
        }

        if (this.state.s_warning) {
          severities.push(2);
        }

        if (this.state.s_info) {
          severities.push(3);
        }
      }

      // Determine the offset
      let offset = 0;
      if (this.state.page > 0)
      {
        offset = this.state.page * this.state.rowsPerPage
      }
      let first = this.state.rowsPerPage;
    
      let totalCount = 0;

      return (
        <div className={classes.root}>
          <Grid container spacing={1} justifyContent="space-between">
            <Grid item>
              <Grid container spacing={2} style={{marginTop: 8, marginBottom: 8}}>
                <Grid item xs={12}>
                  <Typography variant="h6">
                    Audited events
                  </Typography>
                </Grid>
                <Grid item>
                  <SearchField
                    id="accountName"
                    label="Search by account name"
                    value={this.state.accountName}
                    onChange={this.handleChange('accountName')}
                  />

                </Grid>
                <Grid item>
                  <SearchField
                    id="auditType"
                    label="Search by audit type"
                    value={this.state.auditType}
                    onChange={this.handleChange('auditType')}
                  />

                </Grid>
                <Grid item>
                  <SearchField
                    id="auditDetail"
                    label="Search by audit detail"
                    value={this.state.auditDetail}
                    onChange={this.handleChange('auditDetail')}
                  />
                </Grid>
                <Grid item style={AppBindStyles.datePickerField}>
                  <DatePicker
                    autoOk
                    label="From Date"
                    value={this.state.startDate}
                    onChange={(date) => this.setFromDate(date) }
                    format="YYYY-MM-DD"
                    disableFuture
                    renderInput={(params) => <TextField {...params} />}
                  />
                </Grid>
                <Grid item style={AppBindStyles.datePickerField}>
                  <DatePicker
                    autoOk
                    label="To Date"
                    value={this.state.endDate}
                    onChange={(date) => this.setToDate(date) }
                    format="YYYY-MM-DD"
                    disableFuture
                    renderInput={(params) => <TextField {...params} />}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox checked={this.state.s_critical} onChange={this.handleCheckChange('s_critical')} value='s_critical' />
              }
              label="Critical"
            />
            <FormControlLabel
              control={
                <Checkbox checked={this.state.s_error} onChange={this.handleCheckChange('s_error')} value='s_error' />
              }
              label="Error"
            />
            <FormControlLabel
              control={
                <Checkbox checked={this.state.s_warning} onChange={this.handleCheckChange('s_warning')} value='s_warning' />
              }
              label="Warning"
            />
            <FormControlLabel
              control={
                <Checkbox checked={this.state.s_info} onChange={this.handleCheckChange('s_info')} value='s_info' />
              }
              label="Info"
            />
          </FormGroup>
          <QueryRenderer
            environment={abEnvironment.getEnvironment()}
            query={AuditsQuery}
            variables={{startDate: this.state.queryStartDateUtc,
                        endDate: this.state.queryEndDateUtc,
                        accountName: theAccountSearch,
                        severities: severities,
                        auditType: theAuditTypeSearch,
                        auditDetail: theAuditDetailSearch,
                        rlimit: 100,
                        first: first,
                        offset: offset}}
            render={({error, props})=> {
              if (error) {
                return <div>{error.message}</div>
              } else if (props) {
                // Determine offset information
                totalCount = props.getauditentries2.totalCount;

                return (
                  <div>
                    <AuditsList al={props.getauditentries2} history={this.props.history}/>
                    <div style={AppBindStyles.paginationTableFooter}>
                      <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={totalCount}
                        rowsPerPage={this.state.rowsPerPage}
                        page={this.state.page}
                        backIconButtonProps={{
                          'aria-label': 'Previous Page',
                        }}
                        nextIconButtonProps={{
                          'aria-label': 'Next Page',
                        }}
                        onPageChange={this.handleChangePage}
                        onRowsPerPageChange={this.handleChangeRowsPerPage}
                      />                        
                    </div>
                  </div>
                )
              }
              return (
                <LoadingSpinner />
              );
            }}
          />
        </div>
      );
    }
}
  
Audits.propTypes = {
  classes: PropTypes.object.isRequired,
};
  
export default withStyles(styleSheet)(Audits);